/**
 * Toggles the selection state of an entity (team, location, etc.) and automatically
 * manages the selection of all persons associated with that entity.
 *
 * @param {Set<string|number>} localEntityIds - Set of currently selected entity IDs
 * @param {Set<string|number>} localPersonIds - Set of currently selected person IDs
 * @param {Object.<string|number, Array<{personId: string|number, firstName: string, lastName: string}>>} personsByEntity -
 *   Object mapping entity IDs to arrays of person objects associated with that entity
 * @param {string|number} entityId - The ID of the entity to toggle selection for
 *
 * @returns {{updatedEntities: Set<string|number>, updatedPersons: Set<string|number>}}
 *   Object containing the updated Sets of selected entity IDs and person IDs
 *
 * @example
 * // Toggle team selection
 * const { updatedEntities, updatedPersons } = toggleEntitySelection(
 *   new Set(['team1']),
 *   new Set(['person1']),
 *   { team2: [{ personId: 'person2', firstName: 'John', lastName: 'Doe' }] },
 *   'team2'
 * );
 * // Result: updatedEntities = Set(['team1', 'team2']), updatedPersons = Set(['person1', 'person2'])
 */
export const toggleEntitySelection = (
  localEntityIds,
  localPersonIds,
  personsByEntity,
  entityId,
) => {
  const updatedEntities = new Set(localEntityIds);
  const updatedPersons = new Set(localPersonIds);
  const entityPersons = (personsByEntity[entityId] || []).map(
    (person) => person.personId,
  );

  if (updatedEntities.has(entityId)) {
    updatedEntities.delete(entityId);
    entityPersons.forEach((uid) => updatedPersons.delete(uid));
  } else {
    updatedEntities.add(entityId);
    entityPersons.forEach((uid) => updatedPersons.add(uid));
  }
  return {
    updatedEntities,
    updatedPersons,
  };
};

/**
 * Toggles selection of all entities and their associated persons.
 * If all entities are currently selected, deselects everything.
 * If not all are selected, selects all entities and their persons.
 *
 * @param {boolean} areAllEntitiesSelected - Whether all entities are currently selected
 * @param {Array<{[entityIdKey]: string|number}>} entityList - Array of entity objects
 * @param {Object.<string|number, Array<{personId: string|number}>>} personsByEntity -
 *   Object mapping entity IDs to arrays of person objects
 * @param {string} entityIdKey - The key name for the entity ID (e.g., 'teamId', 'locationId')
 *
 * @returns {{updatedEntities: Set<string|number>, updatedPersons: Set<string|number>}}
 *   Object containing the updated Sets of selected entity IDs and person IDs
 *
 * @example
 * // Toggle select all teams
 * const { updatedEntities, updatedPersons } = toggleSelectAllEntities(
 *   false,
 *   [{ teamId: 'team1' }, { teamId: 'team2' }],
 *   { team1: [{ personId: 'person1' }], team2: [{ personId: 'person2' }] },
 *   'teamId'
 * );
 * // Result: updatedEntities = Set(['team1', 'team2']), updatedPersons = Set(['person1', 'person2'])
 */
export const toggleSelectAllEntities = (
  areAllEntitiesSelected,
  entityList,
  personsByEntity,
  entityIdKey,
) => {
  if (areAllEntitiesSelected) {
    return {
      updatedEntities: new Set(),
      updatedPersons: new Set(),
    };
  }

  const updatedEntities = new Set(
    entityList.map((entity) => entity[entityIdKey]),
  );
  const updatedPersons = new Set(
    entityList.flatMap((entity) =>
      (personsByEntity[entity[entityIdKey]] || []).map(
        (person) => person.personId,
      ),
    ),
  );

  return {
    updatedEntities,
    updatedPersons,
  };
};

/**
 * Filters entities based on search text, checking both entity names and associated person names.
 *
 * @param {Object} entity - The entity object to filter
 * @param {string} entity.name - The name of the entity
 * @param {string|number} entity[entityIdKey] - The ID of the entity
 * @param {string} searchText - The search text to filter by
 * @param {Object.<string|number, Array<{firstName: string, lastName: string}>>} personsByEntity -
 *   Object mapping entity IDs to arrays of person objects
 * @param {string} entityIdKey - The key name for the entity ID (e.g., 'teamId', 'locationId')
 *
 * @returns {boolean} Whether the entity matches the search criteria
 *
 * @example
 * // Filter teams by search text
 * const matchesSearch = filterEntity(
 *   { teamId: 'team1', name: 'Marketing Team' },
 *   'market',
 *   { team1: [{ firstName: 'John', lastName: 'Doe' }] },
 *   'teamId'
 * );
 * // Result: true (matches team name)
 */
export const filterEntity = (
  entity,
  searchText,
  personsByEntity,
  entityIdKey,
) => {
  const search = searchText.toLowerCase();

  // Check if entity name matches
  if (entity.name.toLowerCase().includes(search)) {
    return true;
  }

  // Check if any associated person's name matches
  const members = personsByEntity[entity[entityIdKey]] || [];
  return members.some((member) =>
    `${member.firstName} ${member.lastName}`.toLowerCase().includes(search),
  );
};

/**
 * Toggles selection of an individual person and updates the parent entity's selection state accordingly.
 * If all persons in an entity are selected, the entity becomes selected.
 * If not all persons in an entity are selected, the entity becomes deselected.
 *
 * @param {string|number} entityId - The ID of the parent entity
 * @param {string|number} personId - The ID of the person to toggle
 * @param {Set<string|number>} localEntityIds - Set of currently selected entity IDs
 * @param {Set<string|number>} localPersonIds - Set of currently selected person IDs
 * @param {Object.<string|number, Array<{personId: string|number}>>} personsByEntity -
 *   Object mapping entity IDs to arrays of person objects
 *
 * @returns {{updatedEntities: Set<string|number>, updatedPersons: Set<string|number>}}
 *   Object containing the updated Sets of selected entity IDs and person IDs
 *
 * @example
 * // Toggle person selection
 * const { updatedEntities, updatedPersons } = togglePersonSelection(
 *   'team1',
 *   'person1',
 *   new Set(['team2']),
 *   new Set(['person2']),
 *   { team1: [{ personId: 'person1' }, { personId: 'person3' }], team2: [{ personId: 'person2' }] }
 * );
 * // Result: updatedEntities = Set(['team2']), updatedPersons = Set(['person1', 'person2'])
 */
export const togglePersonSelectionByEntity = (
  entityId,
  personId,
  localEntityIds,
  localPersonIds,
  personsByEntity,
) => {
  const updatedEntities = new Set(localEntityIds);
  const updatedPersons = new Set(localPersonIds);

  // Toggle person selection
  if (updatedPersons.has(personId)) {
    updatedPersons.delete(personId);
  } else {
    updatedPersons.add(personId);
  }

  // Check if all persons in the entity are now selected
  const entityPersons = (personsByEntity[entityId] || []).map(
    (person) => person.personId,
  );
  const entityComplete = entityPersons.every((uid) => updatedPersons.has(uid));

  // Update entity selection based on whether all its persons are selected
  if (entityComplete) {
    updatedEntities.add(entityId);
  } else {
    updatedEntities.delete(entityId);
  }

  return {
    updatedEntities,
    updatedPersons,
  };
};
